import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Spacer from 'design-system/Spacer';
import EmmaBetaLogo from 'design-system/EmmaBetaLogo';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useIsSignedInWeb } from 'features/web/hooks/useIsSignedInWeb';
import AvatarFeed from 'features/feed/components/avatar-feed/AvatarFeed';
import { useAppSelector } from 'store/hooks';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import { selectCurrentScreen, selectNavigatorConfig } from 'reducers/selectors';
import { rem } from '../values';
import PayIcon from './PayIcon';
import SaveIcon from './SaveIcon';
import FeedIcon from './FeedIcon';
import BorrowIcon from './BorrowIcon';
import InvestIcon from './InvestIcon';
import NavBarItem from './NavBarItem';
import NetworthIcon from './NetworthIcon';
import BudgetingIcon from './BudgetingIcon';
import RecurringIcon from './RecurringIcon';
import GroupsIcon from './GroupsIcon';
const WebNavigationBar = React.memo(() => {
    const styles = useStyles(styleSet);
    const { sidebarWidth } = useAppFrameDimensions(true);
    const navigation = useMainStackNavigation();
    const { navigateToTab } = useTabNavigation();
    const { isSignedIn } = useIsSignedInWeb();
    const currentScreen = useAppSelector(selectCurrentScreen);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const { showSaveTab, showPayTab, showInvestTab, showBorrowTab, showNetworthTab, showBudgetingTab, showRecurringTab, showGroupTab, } = useAppSelector(selectNavigatorConfig);
    const handleFeed = useCallback(() => {
        navigation.navigateDeprecated('Feed');
    }, []);
    const handleSave = useCallback(() => {
        navigateToTab('Save');
    }, []);
    const handleInvest = useCallback(() => {
        navigateToTab('Invest');
    }, []);
    const handleBorrow = useCallback(() => {
        navigateToTab('Borrow');
    }, []);
    const handleNetworth = useCallback(() => {
        navigateToTab('NetworthTab');
    }, []);
    const handleBudgeting = useCallback(() => {
        navigateToTab('BudgetingTab');
    }, []);
    const handleRecurring = useCallback(() => {
        navigateToTab('RecurringTab');
    }, []);
    // The app's Groups tab is the groups list on web
    const handleGroups = useCallback(() => {
        navigation.navigateDeprecated('GroupsList');
    }, []);
    const { priority } = useAppSelector(selectTabsPriority);
    const tabsMap = useMemo(() => {
        const map = {
            save: showSaveTab && (<NavBarItem title="Save" Icon={SaveIcon} onPress={handleSave} isActive={currentScreen === 'Save'}/>),
            pay: showPayTab && (<NavBarItem title="Pay" Icon={PayIcon} showComingSoon isActive={currentScreen === 'Pay'}/>),
            invest: showInvestTab && (<NavBarItem title="Invest" Icon={InvestIcon} onPress={handleInvest} isActive={currentScreen === 'Invest'}/>),
            borrow: showBorrowTab && (<NavBarItem title="Credit" Icon={BorrowIcon} onPress={handleBorrow} isActive={currentScreen === 'Borrow'}/>),
            networth: showNetworthTab && (<NavBarItem title="Networth" Icon={NetworthIcon} onPress={handleNetworth} isActive={currentScreen === 'NetworthTab'}/>),
            budgeting: showBudgetingTab && (<NavBarItem title="Budgeting" Icon={BudgetingIcon} onPress={handleBudgeting} isActive={currentScreen === 'BudgetingTab'}/>),
            recurring: showRecurringTab && (<NavBarItem title="Recurring" Icon={RecurringIcon} onPress={handleRecurring} isActive={currentScreen === 'RecurringTab'}/>),
            groups: showGroupTab && (<NavBarItem title="Groups" Icon={GroupsIcon} onPress={handleGroups} isActive={currentScreen === 'GroupsList'}/>),
        };
        return map;
    }, [
        currentScreen,
        handleBorrow,
        handleBudgeting,
        handleGroups,
        handleInvest,
        handleNetworth,
        handleRecurring,
        handleSave,
        showBorrowTab,
        showBudgetingTab,
        showGroupTab,
        showInvestTab,
        showNetworthTab,
        showPayTab,
        showRecurringTab,
        showSaveTab,
    ]);
    const hiddenOnThisRoute = currentScreen === 'AuthComplete';
    if (sidebarWidth && isSignedIn && guessedHomeCountry && !hiddenOnThisRoute) {
        return (<View style={[styles.container, { width: sidebarWidth }]}>
        <View style={styles.flex}>
          <AvatarFeed />
          <Spacer.H16 />
          <NavBarItem title="Feed" Icon={FeedIcon} onPress={handleFeed} isActive={currentScreen === 'Feed'}/>

          {priority.map((item) => tabsMap[item] ? (<React.Fragment key={item}>
                <Spacer.H16 />
                {tabsMap[item]}
              </React.Fragment>) : null)}
        </View>
        <View style={styles.logoContainer}>
          <EmmaBetaLogo />
        </View>
      </View>);
    }
    return null;
});
export default WebNavigationBar;
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(32),
        borderRightWidth: 1,
        paddingBottom: rem(40),
        paddingHorizontal: rem(16),
        backgroundColor: colors.background.dark,
        borderRightColor: colors.borders.divider,
    },
    flex: {
        flex: 1,
    },
    logoContainer: {
        paddingLeft: rem(16),
    },
}));
